export interface CameraUploadBatch {
  id: string;
  deviceId: string;
  originalFileName: string;
  status: 'Pending' | 'Extracting' | 'Running' | 'Completed' | 'Failed';
  totalImages: number;
  processedImages: number;
  errorSummary: string | null;
  dateCreated: string;
  dateCompleted: string | null;
}

export interface CameraUploadFolder {
  className: string;
  count: number;
}

export interface CameraUploadClassification {
  id: string;
  className: string;
  confidence: number | null;
  source: 'Inference' | 'Manual';
  boxX1: number | null;
  boxY1: number | null;
  boxX2: number | null;
  boxY2: number | null;
}

export interface CameraUploadImage {
  id: string;
  batchId: string;
  originalFileName: string;
  mimeType: string;
  inferenceStatus: 'Pending' | 'Completed' | 'Failed';
  dateCreated: string;
  capturedAt: string | null;
  width: number | null;
  height: number | null;
  classifications: CameraUploadClassification[];
}

export interface CameraUploadImagePage {
  images: CameraUploadImage[];
  totalCount: number;
  page: number;
  pageSize: number;
}

export const UNCLASSIFIED_FOLDER = '__unclassified__';

function authHeaders(accessToken: string): Record<string, string> {
  return { Authorization: `Bearer ${accessToken}` };
}

async function handle<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(text || `HTTP ${res.status}`);
  }
  if (res.status === 204) return undefined as T;
  return res.json();
}

// XMLHttpRequest, not fetch — fetch has no reliable cross-browser way to
// report upload (request body) progress, only download progress. This
// matters here since a camera-trap zip batch can run several hundred MB and
// take a while to upload.
export async function uploadCameraZip(
  deviceId: string,
  file: File,
  accessToken: string,
  onProgress?: (fraction: number) => void,
): Promise<{ batchId: string }> {
  const form = new FormData();
  form.append('file', file);

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `/api/devices/${deviceId}/camera-uploads`);
    xhr.setRequestHeader('Authorization', `Bearer ${accessToken}`);
    if (onProgress) {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) onProgress(e.loaded / e.total);
      };
    }
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(xhr.response ? JSON.parse(xhr.response) : undefined);
      } else {
        reject(new Error(xhr.responseText || `HTTP ${xhr.status}`));
      }
    };
    xhr.onerror = () => reject(new Error('Network error during upload'));
    xhr.send(form);
  });
}

export async function getCameraUploadBatch(deviceId: string, batchId: string, accessToken: string): Promise<CameraUploadBatch> {
  const res = await fetch(`/api/devices/${deviceId}/camera-uploads/batches/${batchId}`, { headers: authHeaders(accessToken) });
  return handle(res);
}

export async function getCameraUploadFolders(deviceId: string, accessToken: string): Promise<CameraUploadFolder[]> {
  const res = await fetch(`/api/devices/${deviceId}/camera-uploads/folders`, { headers: authHeaders(accessToken) });
  return handle(res);
}

export async function getCameraUploadFolderImages(
  deviceId: string,
  className: string,
  page: number,
  pageSize: number,
  accessToken: string,
): Promise<CameraUploadImagePage> {
  const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
  const res = await fetch(`/api/devices/${deviceId}/camera-uploads/folders/${encodeURIComponent(className)}/images?${params}`, {
    headers: authHeaders(accessToken),
  });
  return handle(res);
}

export async function getCameraUploadImage(deviceId: string, imageId: string, accessToken: string): Promise<CameraUploadImage> {
  const res = await fetch(`/api/devices/${deviceId}/camera-uploads/images/${imageId}`, { headers: authHeaders(accessToken) });
  return handle(res);
}

export function getCameraUploadImageFileUrl(deviceId: string, imageId: string): string {
  return `/api/devices/${deviceId}/camera-uploads/images/${imageId}/file`;
}

export function getCameraUploadThumbnailUrl(deviceId: string, imageId: string, width = 80): string {
  return `/api/devices/${deviceId}/camera-uploads/images/${imageId}/thumbnail?width=${width}`;
}

export async function fetchCameraUploadImageBlob(url: string, accessToken: string): Promise<string> {
  const res = await fetch(url, { headers: authHeaders(accessToken) });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

export async function patchCameraUploadClassifications(
  deviceId: string,
  imageId: string,
  add: string[],
  remove: string[],
  accessToken: string,
): Promise<CameraUploadImage> {
  const res = await fetch(`/api/devices/${deviceId}/camera-uploads/images/${imageId}/classifications`, {
    method: 'PATCH',
    headers: { ...authHeaders(accessToken), 'Content-Type': 'application/json' },
    body: JSON.stringify({ add, remove }),
  });
  return handle(res);
}

// Always creates a NEW box instance for this class — used both to draw the
// first box for a class and to add another instance of a class already
// present (e.g. a 2nd/3rd wallaby in the same photo).
export async function addCameraUploadClassificationBox(
  deviceId: string,
  imageId: string,
  className: string,
  box: { x1: number; y1: number; x2: number; y2: number },
  accessToken: string,
): Promise<CameraUploadImage> {
  const res = await fetch(`/api/devices/${deviceId}/camera-uploads/images/${imageId}/classifications/${encodeURIComponent(className)}/boxes`, {
    method: 'POST',
    headers: { ...authHeaders(accessToken), 'Content-Type': 'application/json' },
    body: JSON.stringify(box),
  });
  return handle(res);
}

// Adjusts one specific box instance in place (drag/resize) — addressed by
// row id since a class can have several boxes.
export async function adjustCameraUploadClassificationBox(
  deviceId: string,
  imageId: string,
  classificationId: string,
  box: { x1: number; y1: number; x2: number; y2: number },
  accessToken: string,
): Promise<CameraUploadImage> {
  const res = await fetch(`/api/devices/${deviceId}/camera-uploads/images/${imageId}/classifications/boxes/${classificationId}`, {
    method: 'PUT',
    headers: { ...authHeaders(accessToken), 'Content-Type': 'application/json' },
    body: JSON.stringify(box),
  });
  return handle(res);
}

// Removes ONE box instance, leaving any other instances of the same class
// (or other classes) on the image untouched.
export async function deleteCameraUploadClassificationBox(
  deviceId: string,
  imageId: string,
  classificationId: string,
  accessToken: string,
): Promise<CameraUploadImage> {
  const res = await fetch(`/api/devices/${deviceId}/camera-uploads/images/${imageId}/classifications/boxes/${classificationId}`, {
    method: 'DELETE',
    headers: authHeaders(accessToken),
  });
  return handle(res);
}

export async function exportCameraUploads(deviceId: string, accessToken: string): Promise<Blob> {
  const res = await fetch(`/api/devices/${deviceId}/camera-uploads/export`, { headers: authHeaders(accessToken) });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.blob();
}

export async function reinferCameraUploads(deviceId: string, accessToken: string): Promise<{ batchId: string }> {
  const res = await fetch(`/api/devices/${deviceId}/camera-uploads/reinfer`, {
    method: 'POST',
    headers: authHeaders(accessToken),
  });
  return handle(res);
}

export async function clearCameraUploads(deviceId: string, accessToken: string): Promise<void> {
  const res = await fetch(`/api/devices/${deviceId}/camera-uploads`, {
    method: 'DELETE',
    headers: authHeaders(accessToken),
  });
  return handle(res);
}
